import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const TERMINAL = ["Paid", "Failed", "Cancelled", "Expired", "Refunded", "Partially Refunded"];

function Shell({ icon, title, children, testid }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid={testid}>
      <div className="glass rounded-2xl p-10 max-w-md w-full text-center space-y-4">
        {icon}
        <h1 className="font-serif text-3xl">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ status: "Pending", amount: null, label: null });
  const [timedOut, setTimedOut] = useState(false);
  const ticks = useRef(0);

  useEffect(() => {
    if (!sessionId) { setTimedOut(true); return; }
    let live = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!live) return;
        setState({ status: data.status, amount: data.amount, label: data.purpose_label });
        if (TERMINAL.includes(data.status)) return;
      } catch (_) {}
      ticks.current += 1;
      if (ticks.current >= 20) { setTimedOut(true); return; }  // ~40s
      setTimeout(poll, 2000);
    };
    poll();
    return () => { live = false; };
  }, [sessionId]);

  const paid = state.status === "Paid";
  const failed = ["Failed", "Cancelled", "Expired"].includes(state.status);

  if (paid) {
    return (
      <Shell testid="payment-success" icon={<CheckCircle2 className="mx-auto text-emerald-400" size={56} />} title="Payment Completed">
        <p className="text-muted-foreground text-sm">
          {state.label ? `${state.label} · ` : ""}{state.amount} received. Thank you.
          A receipt has been added to your reservation.
        </p>
        <button onClick={() => nav("/portal/reservations")} className="btn-gold px-6 py-3 text-sm w-full" data-testid="payment-success-continue">Return to My Reservations</button>
      </Shell>
    );
  }
  if (failed) {
    return (
      <Shell testid="payment-failed" icon={<XCircle className="mx-auto text-red-400" size={56} />} title={`Payment ${state.status}`}>
        <p className="text-muted-foreground text-sm">Your payment did not complete. You can try again from your reservation.</p>
        <button onClick={() => nav("/portal/reservations")} className="btn-ghost-gold px-6 py-3 text-sm w-full">Back to My Reservations</button>
      </Shell>
    );
  }
  return (
    <Shell testid="payment-pending" icon={timedOut ? <Clock className="mx-auto text-gold" size={56} /> : <Loader2 className="mx-auto text-gold animate-spin" size={56} />}
      title={timedOut ? "Payment Processing" : "Confirming Payment"}>
      <p className="text-muted-foreground text-sm">
        {timedOut
          ? "Your payment is still processing. We'll update your folio automatically once it's confirmed."
          : "Please wait while we securely confirm your payment. This only takes a moment."}
      </p>
      <button onClick={() => nav("/portal/reservations")} className="btn-ghost-gold px-6 py-3 text-sm w-full">Back to My Reservations</button>
    </Shell>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <Shell testid="payment-cancel" icon={<AlertTriangle className="mx-auto text-amber-400" size={56} />} title="Payment Cancelled">
      <p className="text-muted-foreground text-sm">You cancelled before completing payment. No charge was made. You can pay anytime from your reservation.</p>
      <button onClick={() => nav("/portal/reservations")} className="btn-gold px-6 py-3 text-sm w-full" data-testid="payment-cancel-back">Back to My Reservations</button>
    </Shell>
  );
}
